import React, { useEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import {
  GearDefs,
  GearYoloboxArt,
  GearZvE10Art,
  GearPtzCamArt,
  GearLumixG7Art,
  GearPtzCtrlArt,
  GearPocket4Art,
  GearCineMoreArt,
} from './GearArt';

// ─── GEAR data array (source order = tab + mobile stack) ────────────
// Copy + placement live in ONE place (spec A6 table). Each entry drives a
// GearCard; `slug` selects the .gear-cell--<slug> layout class in the CSS.
interface GearItem {
  slug: string;
  Art: React.ComponentType;
  chip: string;
  name: string;
  role: string;
  /** Optional real product photo (transparent PNG at /live/gear/<slug>.png).
   *  When the file exists it replaces the SVG line-art with a photoreal image
   *  on a higher translateZ plane (stronger 3D parallax); on 404 it falls back
   *  to <Art/> so the section is never broken. */
  img?: string;
}

const GEAR: GearItem[] = [
  {
    slug: 'yolobox',
    Art: GearYoloboxArt,
    chip: 'LIVE 4K SWITCHER',
    name: 'YoloBox Ultra',
    role: 'Live 4K switching & streaming — the brain of the broadcast.',
    img: '/live/gear/yolobox.png',
  },
  {
    slug: 'zve10',
    Art: GearZvE10Art,
    chip: 'CINEMA BODY',
    name: 'Sony ZV-E10',
    role: 'Cinema-grade 4K body for the faces and the vows.',
    img: '/live/gear/zve10.png',
  },
  {
    slug: 'ptzcam',
    Art: GearPtzCamArt,
    chip: 'ROBOTIC PTZ',
    name: 'PTZ Camera',
    role: 'Silent robotic pan-tilt-zoom that never misses a moment.',
    img: '/live/gear/ptzcam.png',
  },
  {
    slug: 'lumix',
    Art: GearLumixG7Art,
    chip: 'CINEMA BODY',
    name: 'Lumix G7',
    role: 'A second cinematic angle, running the whole ceremony.',
    img: '/live/gear/lumix.png',
  },
  {
    slug: 'ptzctrl',
    Art: GearPtzCtrlArt,
    chip: 'LIVE OPERATION',
    name: 'PTZ Controller',
    role: 'Our operator frames every shot live, in real time.',
    img: '/live/gear/ptzctrl.png',
  },
  {
    slug: 'pocket',
    Art: GearPocket4Art,
    chip: 'POCKET GIMBAL',
    name: 'DJI Pocket 4',
    role: 'Pocket gimbal for the intimate, in-close details.',
    img: '/live/gear/pocket.png',
  },
  {
    slug: 'more',
    Art: GearCineMoreArt,
    chip: 'AND MORE',
    name: 'And the crew',
    role: 'Lights, audio, backup links — and the team who run it all.',
    img: '/live/gear/more.png',
  },
];

// ─── fine-pointer detection (SSR-guarded, computed once) ────────────
// matchMedia is only touched inside an effect (never during the server
// render path). Cards start with tilt disabled and enable it after mount
// on hover-capable, fine-pointer devices only.
function useFinePointer(): boolean {
  const [fine, setFine] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setFine(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setFine(e.matches);
    // addEventListener is the modern API; guard for older Safari.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    return undefined;
  }, []);
  return fine;
}

const SPRING = { stiffness: 220, damping: 22, mass: 0.4 } as const;

// ─── GearCard — 3D tilt engine (motion values, zero re-render on move) ─
interface GearCardProps {
  item: GearItem;
  reduced: boolean;
  finePointer: boolean;
}

function GearCard({ item, reduced, finePointer }: GearCardProps): React.ReactElement {
  const { slug, Art, chip, name, role, img } = item;
  const ref = useRef<HTMLDivElement>(null);
  // Photo replaces the SVG when the file loads; on 404 we fall back to <Art/>.
  const [imgOk, setImgOk] = useState<boolean>(!!img);

  // Hero (2×2) tile tilts a touch less than the small tiles.
  const ROT = slug === 'yolobox' ? 6 : 8;

  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [ROT, -ROT]), SPRING);
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-ROT, ROT]), SPRING);

  const tiltEnabled = !reduced && finePointer;
  const showPhoto = !!img && imgOk;

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!tiltEnabled) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    const nx = (e.clientX - r.left) / r.width;
    const ny = (e.clientY - r.top) / r.height;
    px.set(nx - 0.5);
    py.set(ny - 0.5);
    el.style.setProperty('will-change', 'transform');
    // Feeds the CSS cursor rim-light + sheen (.gear-card::before / .gear-sheen).
    el.style.setProperty('--gx', `${nx * 100}%`);
    el.style.setProperty('--gy', `${ny * 100}%`);
  };

  const onPointerLeave = (): void => {
    px.set(0);
    py.set(0);
    ref.current?.style.removeProperty('will-change');
  };

  // Depth reveal (y + z + rotateX) that shares the page's motion vocabulary;
  // flat opacity-only fallback when reduced-motion is requested.
  const gearReveal: Variants = reduced
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 34, z: -60, rotateX: 6 },
        visible: {
          opacity: 1,
          y: 0,
          z: 0,
          rotateX: 0,
          transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
        },
      };

  return (
    <motion.div
      ref={ref}
      className={`gear-card gear-cell--${slug}`}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      variants={gearReveal}
      style={{
        rotateX: tiltEnabled ? rotateX : 0,
        rotateY: tiltEnabled ? rotateY : 0,
        transformStyle: 'preserve-3d',
      }}
    >
      {/* z0 — glass panel background (mirrors .live-glass) */}
      <div className="gear-card-bg" aria-hidden="true" />

      {showPhoto ? (
        /* translateZ 32px — the real product photo floats forward for 3D parallax */
        <div className="gear-photo" style={{ transform: 'translateZ(32px)' }}>
          <img src={img} alt={name} loading="lazy" onError={() => setImgOk(false)} />
        </div>
      ) : (
        /* translateZ 40px — the device line-art fallback, floats forward in 3D space */
        <div className="gear-art" style={{ transform: 'translateZ(40px)' }}>
          <Art />
        </div>
      )}

      {/* z5 — cursor-tracked diagonal sheen */}
      <div className="gear-sheen" aria-hidden="true" />

      {/* translateZ 20px — chip eyebrow + Playfair name + role */}
      <div className="gear-meta" style={{ transform: 'translateZ(20px)' }}>
        <span className="gear-chip">{chip}</span>
        <h3 className="gear-name font-playfair">{name}</h3>
        <p className="gear-role">{role}</p>
      </div>
    </motion.div>
  );
}

// ─── GearShowcase — bento container + stagger orchestration ──────────
export default function GearShowcase({
  reduced,
}: {
  reduced: boolean | null;
}): React.ReactElement {
  const hookReduced = useReducedMotion();
  // Prop wins when explicitly passed; otherwise fall back to the hook.
  const isReduced = reduced ?? hookReduced ?? false;
  const finePointer = useFinePointer();

  const container: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.09 },
    },
  };

  return (
    <motion.div
      className="gear-bento"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={container}
    >
      {/* Shared SVG gradient defs — rendered ONCE for all sibling art. */}
      <GearDefs />

      {GEAR.map((item) => (
        <GearCard
          key={item.slug}
          item={item}
          reduced={isReduced}
          finePointer={finePointer}
        />
      ))}
    </motion.div>
  );
}
